/**
 * Embed App
 *
 * Wrapper for iframe embedding with URL configuration
 */

import { useState } from "react";
import { ConciergeWidget } from "@chekmenu/concierge-core";
import { useUrlConfig, configToProps } from "./hooks/useUrlConfig";
import { useIframeResize } from "./hooks/useIframeResize";

/**
 * Error display component
 */
function ErrorDisplay({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: "20px",
        color: "#991b1b",
        backgroundColor: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "8px",
        fontFamily: "system-ui, sans-serif",
        fontSize: "14px",
      }}
    >
      <strong>Configuration Error:</strong>
      <br />
      {message}
    </div>
  );
}

/**
 * Widget wrapper that tracks open state for iframe resize
 */
function WidgetWrapper(props: React.ComponentProps<typeof ConciergeWidget>) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useIframeResize<HTMLDivElement>(isOpen);

  const handleOpen = () => {
    setIsOpen(true);
    props.onOpen?.();
  };

  const handleClose = () => {
    setIsOpen(false);
    props.onClose?.();
  };

  return (
    <div ref={containerRef}>
      <ConciergeWidget {...props} onOpen={handleOpen} onClose={handleClose} />
    </div>
  );
}

/**
 * Main embed app component
 */
export function EmbedApp() {
  const config = useUrlConfig();

  if (!config.isValid) {
    return <ErrorDisplay message={config.error || "Invalid configuration"} />;
  }

  const props = configToProps(config);

  if (!props) {
    return <ErrorDisplay message="Failed to parse configuration" />;
  }

  return <WidgetWrapper {...props} />;
}
